import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiSearch, FiX } from "react-icons/fi";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import classNames from "../common/classNames";
import { trapTabKey } from "../common/focusTrap";

const getImageSource = (image) => (typeof image === "string" ? image : image?.src || image?.url);

const SearchOverlay = ({ open, onClose, products = [], className }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const overlayRef = useRef(null);
  const returnFocusRef = useRef(null);
  const navigate = useNavigate();

  const suggestions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery.length < 2) return [];

    return products
      .filter((product) => {
        const searchable = [
          product.name,
          product.category,
          product.subtitle,
          ...(Array.isArray(product.tags) ? product.tags : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(normalizedQuery);
      })
      .slice(0, 6);
  }, [products, query]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    document.body.classList.add("has-open-search");
    inputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      else trapTabKey(event, overlayRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("has-open-search");
      document.removeEventListener("keydown", handleKeyDown);
      returnFocusRef.current?.focus?.();
    };
  }, [onClose, open]);

  if (!open) return null;

  const submitSearch = (event) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;
    onClose?.();
    navigate(`/search?q=${encodeURIComponent(normalizedQuery)}`);
  };

  return (
    <section ref={overlayRef} className={classNames("search-overlay", className)} role="dialog" aria-modal="true" aria-label="Search products" tabIndex="-1">
      <header className="search-overlay__header site-container">
        <form className="search-overlay__form" role="search" onSubmit={submitSearch}>
          <FiSearch aria-hidden="true" />
          <label className="visually-hidden" htmlFor="site-search-overlay">
            Search products
          </label>
          <input
            ref={inputRef}
            id="site-search-overlay"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search spices, seasonings and more"
            autoComplete="off"
          />
          {query && (
            <button className="search-overlay__clear" type="button" onClick={() => setQuery("")} aria-label="Clear search">
              <FiX aria-hidden="true" />
            </button>
          )}
          <Button type="submit" size="small" iconOnly aria-label="Submit search">
            <FiArrowRight aria-hidden="true" />
          </Button>
        </form>
        <button className="search-overlay__close" type="button" onClick={onClose} aria-label="Close search">
          <FiX aria-hidden="true" />
        </button>
      </header>

      <div className="search-overlay__results site-container" aria-live="polite">
        {query.trim().length >= 2 && suggestions.length === 0 && (
          <EmptyState
            icon={FiSearch}
            title="No matching products"
            description="Try another ingredient, blend, or category."
          />
        )}
        {suggestions.length > 0 && (
          <>
            <p className="search-overlay__eyebrow">Suggested products</p>
            <ul className="search-overlay__suggestions">
              {suggestions.map((product) => {
                const image = getImageSource(product.images?.[0] || product.image);
                return (
                  <li key={product.id || product.slug}>
                    <Link to={`/products/${product.slug}`} onClick={onClose}>
                      {image && <img src={image} alt="" loading="lazy" />}
                      <span>
                        <strong>{product.name}</strong>
                        {product.category && <small>{product.category}</small>}
                      </span>
                      <FiArrowRight aria-hidden="true" />
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Button variant="text" type="button" onClick={submitSearch}>
              View all results for “{query.trim()}”
            </Button>
          </>
        )}
        {!query.trim() && (
          <div className="search-overlay__prompt">
            <p className="search-overlay__eyebrow">Search the pantry</p>
            <p>Find a spice by name, flavor, cuisine, or ingredient.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchOverlay;
